import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from 'primereact/card';
import { TabView, TabPanel } from 'primereact/tabview';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { Message } from 'primereact/message';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { FinanceBook, FinanceCompany } from '../types';
import { financeBooksAPI, financeCompaniesAPI } from '../services/api';

type BookFormState = {
  name: string;
  companyId: string;
  year: number;
};

const getCompanyId = (book: FinanceBook) => {
  const c = book.companyId as any;
  return typeof c === 'string' ? c : c?._id;
};

const formatDate = (value?: string) => {
  if (!value) return '-';
  try {
    const d = new Date(value);
    return d.toLocaleDateString('sr-RS');
  } catch {
    return '-';
  }
};

const FinanceBooks: React.FC = () => {
  const navigate = useNavigate();

  const [companies, setCompanies] = useState<FinanceCompany[]>([]);
  const [books, setBooks] = useState<FinanceBook[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [search, setSearch] = useState('');

  const [dialogVisible, setDialogVisible] = useState(false);
  const [editingBook, setEditingBook] = useState<FinanceBook | null>(null);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState<BookFormState>({
    name: '',
    companyId: '',
    year: new Date().getFullYear()
  });

  const loadAll = async () => {
    setLoading(true);
    try {
      const [companiesData, booksData] = await Promise.all([
        financeCompaniesAPI.getAll(),
        financeBooksAPI.getAll()
      ]);
      setCompanies(companiesData);
      setBooks(booksData);
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Greška pri učitavanju knjiga';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const companiesWithBooks = useMemo(() => {
    const map = new Map<string, { company: FinanceCompany; books: FinanceBook[] }>();
    companies.forEach((c) => map.set(c._id, { company: c, books: [] }));
    books.forEach((b) => {
      const cid = getCompanyId(b);
      if (!cid) return;
      const existing = map.get(cid);
      const company = typeof b.companyId === 'string' ? map.get(cid)?.company : (b.companyId as FinanceCompany);
      if (!existing && company) map.set(cid, { company, books: [] });
      (map.get(cid)?.books || []).push(b);
    });
    return Array.from(map.values()).filter((x) => x.books.length > 0 || companies.length > 0);
  }, [companies, books]);

  const filteredBooksForCompany = (companyId: string) => {
    const q = search.trim().toLowerCase();
    const companyBooks = books.filter((b) => getCompanyId(b) === companyId);
    if (!q) return companyBooks;
    return companyBooks.filter((b) => {
      const code = (b.code || '').toLowerCase();
      const name = (b.name || '').toLowerCase();
      const year = String(b.year || '').toLowerCase();
      return code.includes(q) || name.includes(q) || year.includes(q);
    });
  };

  const openCreateDialog = () => {
    setEditingBook(null);
    const defaultCompanyId = companiesWithBooks[0]?.company?._id || companies[0]?._id || '';
    setFormData({
      name: '',
      companyId: defaultCompanyId,
      year: new Date().getFullYear()
    });
    setError('');
    setDialogVisible(true);
  };

  const openEditDialog = (book: FinanceBook) => {
    setEditingBook(book);
    setFormData({
      name: book.name || '',
      companyId: getCompanyId(book) || '',
      year: book.year || new Date().getFullYear()
    });
    setError('');
    setDialogVisible(true);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) return setError('Naziv je obavezan');
    if (!formData.companyId) return setError('Firma je obavezna');
    if (!formData.year) return setError('Godina je obavezna');

    try {
      setError('');
      if (editingBook) {
        await financeBooksAPI.update(editingBook._id, {
          name: formData.name,
          companyId: formData.companyId,
          year: formData.year
        });
      } else {
        await financeBooksAPI.create({
          name: formData.name,
          companyId: formData.companyId,
          year: formData.year
        });
      }
      setDialogVisible(false);
      await loadAll();
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Greška pri čuvanju knjige';
      setError(msg);
    }
  };

  const handleDelete = (book: FinanceBook) => {
    confirmDialog({
      message: `Da li ste sigurni da želite da obrišete knjigu "${book.name}"?`,
      header: 'Potvrda brisanja',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: 'Obriši',
      rejectLabel: 'Otkaži',
      accept: async () => {
        try {
          await financeBooksAPI.delete(book._id);
          await loadAll();
        } catch (err: any) {
          const msg = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Greška pri brisanju knjige';
          setError(msg);
        }
      }
    });
  };

  const iconBodyTemplate = (rowData: FinanceBook) => {
    const bg = rowData.color || '#3b82f6';
    const icon = rowData.icon ? (rowData.icon.includes(' ') ? rowData.icon : `pi ${rowData.icon}`) : 'pi pi-book';
    return (
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: 10,
          backgroundColor: bg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <i className={icon} style={{ color: '#fff', fontSize: '1.1rem' }} />
      </div>
    );
  };

  const companyBodyTemplate = (rowData: FinanceBook) => {
    const c = rowData.companyId as any;
    if (!c) return '-';
    if (typeof c === 'string') {
      const found = companies.find((x) => x._id === c);
      return found ? `${found.name} (${found.city})` : c;
    }
    return `${c.name} (${c.city})`;
  };

  const documentsCountTemplate = (rowData: FinanceBook) => {
    const n = rowData.documentCount ?? 0;
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 26,
          height: 26,
          borderRadius: 13,
          background: '#e5e7eb',
          color: '#111827',
          fontWeight: 700,
          fontSize: 12
        }}
      >
        {n}
      </span>
    );
  };

  const actionsBodyTemplate = (rowData: FinanceBook) => {
    return (
      <div className="flex gap-2">
        <Button
          icon="pi pi-eye"
          className="p-button-text p-button-sm"
          onClick={() => navigate(`/finance/books/${rowData._id}`)}
          aria-label="Pregled"
        />
        <Button
          icon="pi pi-pencil"
          className="p-button-text p-button-sm"
          onClick={() => openEditDialog(rowData)}
          aria-label="Izmeni"
        />
        <Button
          icon="pi pi-trash"
          className="p-button-text p-button-sm p-button-danger"
          onClick={() => handleDelete(rowData)}
          aria-label="Obriši"
        />
      </div>
    );
  };

  const dialogFooter = (
    <div className="flex justify-content-end gap-2">
      <Button label="Otkaži" className="p-button-text" onClick={() => setDialogVisible(false)} />
      <Button label={editingBook ? 'Sačuvaj' : 'Kreiraj'} className="p-button-success" onClick={handleSubmit} />
    </div>
  );

  return (
    <div className="p-4">
      <ConfirmDialog />

      <Card title="Knjige delovodnika">
        <div className="flex justify-content-between align-items-center flex-wrap gap-3 mb-3">
          <Button
            label="Dodaj knjigu"
            icon="pi pi-plus"
            className="p-button-success"
            onClick={openCreateDialog}
          />

          <span className="p-input-icon-left">
            <i className="pi pi-search" />
            <InputText
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Pretraži knjige..."
              style={{ width: 320, maxWidth: '80vw' }}
            />
          </span>
        </div>

        {error && (
          <div className="mb-3">
            <Message severity="error" text={error} />
          </div>
        )}

        <TabView activeIndex={activeTab} onTabChange={(e) => setActiveTab(e.index)}>
          {companiesWithBooks.map(({ company }, idx) => {
            const list = filteredBooksForCompany(company._id);
            const header = (
              <div className="flex align-items-center gap-2">
                <i className="pi pi-building" />
                <span style={{ fontWeight: 600 }}>{company.name}</span>
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 22,
                    height: 22,
                    borderRadius: 11,
                    background: '#22c55e',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: 12
                  }}
                >
                  {list.length}
                </span>
              </div>
            );

            return (
              <TabPanel key={company._id || idx} header={header}>
                <div className="mb-3" style={{ color: '#6b7280' }}>
                  <div style={{ fontWeight: 700 }}>{company.name}</div>
                  <div>
                    {company.city}, {company.country} • {company.address}
                  </div>
                </div>

                <DataTable
                  value={list}
                  loading={loading}
                  paginator
                  rows={10}
                  rowsPerPageOptions={[10, 20, 50]}
                  responsiveLayout="stack" breakpoint="768px"
                  emptyMessage="Nema knjiga"
                >
                  <Column header="Ikona" body={iconBodyTemplate} style={{ width: '90px' }} />
                  <Column field="code" header="Šifra" sortable />
                  <Column field="name" header="Naziv" sortable />
                  <Column header="Firma" body={companyBodyTemplate} />
                  <Column field="year" header="Godina" sortable style={{ width: '110px' }} />
                  <Column header="Datum kreiranja" body={(row: FinanceBook) => formatDate(row.createdAt)} sortable />
                  <Column header="Broj dokumenata" body={documentsCountTemplate} style={{ width: '160px' }} />
                  <Column header="Akcije" body={actionsBodyTemplate} style={{ width: '180px' }} />
                </DataTable>
              </TabPanel>
            );
          })}
        </TabView>
      </Card>

      <Dialog
        header={editingBook ? 'Izmeni knjigu' : 'Nova knjiga'}
        visible={dialogVisible}
        style={{ width: '720px', maxWidth: '95vw' }}
        onHide={() => setDialogVisible(false)}
        footer={dialogFooter}
        modal
      >
        {error && (
          <div className="mb-3">
            <Message severity="error" text={error} />
          </div>
        )}

        <div className="p-fluid formgrid grid">
          <div className="field col-12 md:col-6">
            <label htmlFor="book-name">Naziv *</label>
            <InputText
              id="book-name"
              value={formData.name}
              onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
            />
          </div>

          <div className="field col-12 md:col-6">
            <label htmlFor="book-company">Firma</label>
            <Dropdown
              id="book-company"
              value={formData.companyId}
              options={companies.map((c) => ({ label: c.name, value: c._id }))}
              onChange={(e) => setFormData((p) => ({ ...p, companyId: e.value }))}
              placeholder="Izaberite firmu"
              appendTo="self"
              filter
              showClear
            />
          </div>

          <div className="field col-12 md:col-6">
            <label htmlFor="book-year">Godina</label>
            <InputNumber
              inputId="book-year"
              value={formData.year}
              onValueChange={(e) => setFormData((p) => ({ ...p, year: e.value || new Date().getFullYear() }))}
              useGrouping={false}
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default FinanceBooks;


